import { useEffect, useMemo, useState } from 'react';

import useDependentsQuery from '@/modules/user/components/ManageProfile/components/Dependents/queries/useDependents.query';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';

import useCategorySpendLimits from '../CategorySpendLimitsTable/hooks/useCategorySpendLimits';
import { useSpendLimitsData } from '../SpendLimitsTable/hooks/useSpendLimitsData';
import { useSpendLimitsPeriod } from '../SpendLimitsTable/hooks/useSpendLimitsPeriod';
import useGetPersonSpendLimitsQuery from '../SpendLimitsTable/queries/useGetPersonSpendLimits.query';

export enum EnrollmentDetailsToggleOptions {
  ACTIVITIES = 'Activity & Processing',
  SPEND_LIMITS = 'Spend Limits',
}

interface UseSpendLimitsToggleParams {
  accountId: string;
}

export const useSpendLimitsToggle = ({ accountId }: UseSpendLimitsToggleParams) => {
  const [selectedOption, setSelectedOption] = useState(EnrollmentDetailsToggleOptions.ACTIVITIES);
  const [hadSpendLimits, setHadSpendLimits] = useState(false);
  const [hadCategorySpendLimits, setHadCategorySpendLimits] = useState(false);
  const { employee } = useEmployeeStore();
  const { formattedData: dependents, isLoading: isDependentsLoading } = useDependentsQuery();

  const {
    showPeriodDropdown,
    periodOptions,
    selectedPeriod,
    handlePeriodChange,
    isPeriodLoading,
    isPeriodReady,
  } = useSpendLimitsPeriod({ accountId });

  const { personSpendLimits, isLoading: isPersonSpendLimitsLoading } = useGetPersonSpendLimitsQuery({
    accountId,
    periodFrom: selectedPeriod,
    enabled: isPeriodReady,
  });

  const spendLimitsData = useSpendLimitsData({
    personSpendLimits,
    dependents,
    employeeId: employee?.id,
    employeeName: `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim(),
  });

  const hasSpendLimits = useMemo(() => {
    const hasData = personSpendLimits && personSpendLimits.length > 0;
    if (hasData && !hadSpendLimits) {
      setHadSpendLimits(true);
    }
    return hasData || hadSpendLimits;
  }, [personSpendLimits, hadSpendLimits]);

  const {
    categorySpendLimitsData,
    isCategorySpendLimitsLoading,
    hasCategorySpendLimits,
    showPeriodDropdown: showCategoryPeriodDropdown,
    periodOptions: categoryPeriodOptions,
    selectedPeriod: categorySelectedPeriod,
    handlePeriodChange: handleCategoryPeriodChange,
    isPeriodLoading: isCategoryPeriodLoading,
  } = useCategorySpendLimits({ accountId });

  const showCategorySpendLimits = useMemo(() => {
    if (hasCategorySpendLimits && !hadCategorySpendLimits) {
      setHadCategorySpendLimits(true);
    }
    return hasCategorySpendLimits || hadCategorySpendLimits;
  }, [hasCategorySpendLimits, hadCategorySpendLimits]);

  useEffect(() => {
    setHadSpendLimits(false);
    setHadCategorySpendLimits(false);
    setSelectedOption(EnrollmentDetailsToggleOptions.ACTIVITIES);
  }, [accountId]);

  const toggleOptions = useMemo(() => {
    const options = [EnrollmentDetailsToggleOptions.ACTIVITIES];
    if (hasSpendLimits || showCategorySpendLimits) {
      options.push(EnrollmentDetailsToggleOptions.SPEND_LIMITS);
    }
    return options;
  }, [hasSpendLimits, showCategorySpendLimits]);

  return {
    selectedOption,
    setSelectedOption,
    toggleOptions,
    hasSpendLimits,
    showCategorySpendLimits,
    spendLimitsData,
    isLoading: isPersonSpendLimitsLoading || isDependentsLoading,
    showPeriodDropdown,
    periodOptions,
    selectedPeriod,
    handlePeriodChange,
    isPeriodLoading,
    categorySpendLimitsData,
    isCategorySpendLimitsLoading,
    showCategoryPeriodDropdown,
    categoryPeriodOptions,
    categorySelectedPeriod,
    handleCategoryPeriodChange,
    isCategoryPeriodLoading,
  };
};
